/**
 * Update Notification Composable
 * Manages PWA update prompts with smart user activity tracking
 */

import { ref, shallowRef } from 'vue'

export interface UpdateNotificationOptions {
  title?: string
  message?: string
  features?: string[]
  onUpdate?: () => void
  onLater?: () => void
}

// Global state for update notification
const isUpdateAvailable = ref(false)
const updateOptions = shallowRef<UpdateNotificationOptions>({})
const serviceWorkerRegistration = shallowRef<ServiceWorkerRegistration | null>(null)
const newWorker = shallowRef<ServiceWorker | null>(null)
const newVersion = ref<string | null>(null)

// Track user activity to determine if they're actively using the app
const lastActivityTime = ref(Date.now())
const MIN_ACTIVE_TIME = 2 * 60 * 1000 // 2 minutes of activity before showing updates

// Update activity timestamp on user interaction
if (typeof window !== 'undefined') {
  const updateActivity = () => {
    lastActivityTime.value = Date.now()
  }

  // Track various user interactions
  window.addEventListener('click', updateActivity, { passive: true })
  window.addEventListener('scroll', updateActivity, { passive: true })
  window.addEventListener('keydown', updateActivity, { passive: true })
  window.addEventListener('touchstart', updateActivity, { passive: true })
}

export function useUpdateNotification() {
  /**
   * Show the update notification modal
   */
  const showUpdateNotification = (
    registration?: ServiceWorkerRegistration,
    worker?: ServiceWorker,
    options?: UpdateNotificationOptions,
    version?: string
  ) => {
    // Check if this version was already dismissed
    const lastDismissedVersion = localStorage.getItem('lastDismissedVersion')
    if (version && lastDismissedVersion === version) {
      console.log(`[Update] Skipped: version ${version} was already dismissed`)
      return
    }

    if (registration) {
      serviceWorkerRegistration.value = registration
    }
    if (worker) {
      newWorker.value = worker
    }
    if (options) {
      updateOptions.value = options
    }
    if (version) {
      newVersion.value = version
    }
    isUpdateAvailable.value = true
    console.log('[Update] Update notification shown')
  }

  /**
   * Hide the update notification modal
   */
  const hideUpdateNotification = () => {
    isUpdateAvailable.value = false
  }

  /**
   * Handle the update action
   * Ensures we jump to the LATEST version, even if multiple updates are pending
   */
  const performUpdate = async () => {
    // Call custom update handler if provided
    if (updateOptions.value.onUpdate) {
      updateOptions.value.onUpdate()
    }

    // Clear the last dismissed version so the new version can be tracked
    localStorage.removeItem('lastDismissedVersion')

    try {
      // Force check for the latest service worker before updating
      if (serviceWorkerRegistration.value) {
        console.log('[Update] Checking for latest service worker...')
        try {
          await serviceWorkerRegistration.value.update()
        } catch (updateError) {
          // Handle InvalidStateError - SW might be in bad state
          if (updateError instanceof DOMException && updateError.name === 'InvalidStateError') {
            console.warn('[Update] Service Worker in invalid state - forcing reload')
            window.location.reload()
            return
          }
          throw updateError
        }
      }

      // Get all waiting service workers and activate the newest one
      const registration = await navigator.serviceWorker.getRegistration()
      const waitingWorker = registration?.waiting || newWorker.value

      if (waitingWorker) {
        console.log('[Update] Sending SKIP_WAITING to service worker')
        waitingWorker.postMessage({ type: 'SKIP_WAITING' })

        // Reload after a short delay to allow SW to take control
        setTimeout(() => {
          console.log('[Update] Reloading page')
          window.location.reload()
        }, 500)
      } else {
        // No service worker waiting - just reload to get fresh content
        console.log('[Update] No waiting service worker - reloading page')
        window.location.reload()
      }
    } catch (error) {
      console.error('[Update] Failed to update service worker:', error)
      // Fallback: just reload the page
      window.location.reload()
    }
  }

  /**
   * Handle the later/defer action
   */
  const deferUpdate = () => {
    // Call custom later handler if provided
    if (updateOptions.value.onLater) {
      updateOptions.value.onLater()
    }

    hideUpdateNotification()

    // Store the dismissed version to prevent showing the same version again
    if (newVersion.value) {
      localStorage.setItem('lastDismissedVersion', newVersion.value)
      console.log(`[Update] Dismissed for version: ${newVersion.value}`)
    }

    // Store timestamp to avoid showing again too soon
    localStorage.setItem('lastUpdateDeferred', Date.now().toString())
  }

  /**
   * Check if we should show the update notification
   * Only show if:
   * 1. User hasn't deferred recently (24 hours)
   * 2. User has been actively using the app
   * 3. There's a specific update message (not just returning from idle)
   */
  const shouldShowUpdate = (hasSpecificMessage = false): boolean => {
    // Check if user deferred the update
    const lastDeferred = localStorage.getItem('lastUpdateDeferred')
    if (lastDeferred) {
      const deferredTime = parseInt(lastDeferred, 10)
      const hoursSinceDeferred = (Date.now() - deferredTime) / (1000 * 60 * 60)

      // Don't show again if deferred within last 24 hours
      if (hoursSinceDeferred < 24) {
        return false
      }
    }

    // Check if user has been actively using the app
    const timeSinceLastActivity = Date.now() - lastActivityTime.value
    const isActiveUser = timeSinceLastActivity < MIN_ACTIVE_TIME

    // Only show if user is active OR there's a specific message
    return isActiveUser || hasSpecificMessage
  }

  return {
    // State
    isUpdateAvailable,
    updateOptions,
    newVersion,

    // Actions
    showUpdateNotification,
    hideUpdateNotification,
    performUpdate,
    deferUpdate,
    shouldShowUpdate,
  }
}
